import { Button, mergeClasses } from "@fluentui/react-components";
import {
  ArrowUploadRegular,
  ChatMultipleRegular,
  DismissRegular,
  FolderRegular,
} from "@fluentui/react-icons";
import { ALLOWED_FILE_EXTENSIONS } from "common/constants";
import { DragEvent, useRef, useState } from "react";
import { useSharedDialogContentStyles } from "theme/sharedStyles";

import { useContextTabStyles } from "./ContextTab.styles";
import { IConnectionSourceDef, IContextTabProps } from "./ContextTab.types";

const CONNECTION_SOURCES: IConnectionSourceDef[] = [
  {
    slug: "sharepoint",
    label: "SharePoint",
    description: "Sync the engagement library",
    icon: <FolderRegular />,
    comingSoon: true,
  },
  {
    slug: "teams",
    label: "Microsoft Teams",
    description: "Pull files & channel docs",
    icon: <ChatMultipleRegular />,
    comingSoon: true,
  },
];

export const ContextTab = ({
  isSaving,
  connectedSources,
  stagedFiles,
  onToggleSource,
  onFilesSelected,
  onRemoveFile,
  existingDocuments,
  onRemoveExistingDocument,
}: IContextTabProps): JSX.Element => {
  const styles = useContextTabStyles();
  const contentStyles = useSharedDialogContentStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>("");

  const processFiles = (files: File[]): void => {
    const allowedLower = ALLOWED_FILE_EXTENSIONS.map((ext) =>
      ext.toLowerCase().startsWith(".")
        ? ext.toLowerCase()
        : `.${ext.toLowerCase()}`,
    );
    const valid = files.filter((f) => {
      const ext = `.${f.name.split(".").pop()?.toLowerCase() ?? ""}`;
      return allowedLower.includes(ext);
    });

    const seenNames = new Set(stagedFiles.map((f) => f.name));
    const newFiles: File[] = [];
    const duplicates: string[] = [];
    for (const f of valid) {
      if (seenNames.has(f.name)) {
        duplicates.push(f.name);
        continue;
      }
      seenNames.add(f.name);
      newFiles.push(f);
    }

    if (newFiles.length > 0) {
      onFilesSelected(newFiles);
    }

    if (duplicates.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicates));
      setFileError(
        uniqueDuplicates.length === 1
          ? `A file named "${uniqueDuplicates[0]}" is already selected.`
          : `These files are already selected: ${uniqueDuplicates.join(", ")}.`,
      );
    } else if (valid.length === 0 && files.length > 0) {
      setFileError(
        `Please select files with the following extensions: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`,
      );
    } else {
      setFileError("");
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    if (isSaving) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    if (isSaving) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  const openFilePicker = (): void => {
    if (!isSaving) fileInputRef.current?.click();
  };

  const acceptAttr = ALLOWED_FILE_EXTENSIONS.map((ext) =>
    ext.startsWith(".") ? ext : `.${ext}`,
  ).join(",");

  return (
    <div className={contentStyles.tabContent} role="tabpanel">
      <span id="connection-sources-heading" className={styles.contextHeading}>
        Workspace Documents
      </span>

      <div
        className={styles.connectionGrid}
        role="group"
        aria-labelledby="connection-sources-heading"
      >
        {CONNECTION_SOURCES.map((source) => {
          const connected = connectedSources.includes(source.slug);
          const comingSoon = !!source.comingSoon;
          const interactive = !comingSoon && !isSaving;
          return (
            <div
              key={source.slug}
              className={mergeClasses(
                styles.connectionCard,
                comingSoon && styles.connectionCardComingSoon,
                connected && styles.connectionCardSelected,
              )}
              onClick={() => interactive && onToggleSource(source.slug)}
              role="checkbox"
              aria-checked={comingSoon ? false : connected}
              aria-disabled={comingSoon}
              tabIndex={comingSoon ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  if (interactive) onToggleSource(source.slug);
                }
              }}
            >
              <div
                className={mergeClasses(
                  styles.connectionIconTile,
                  connected && styles.connectionIconTileSelected,
                )}
              >
                {source.icon}
              </div>
              <div className={styles.connectionMeta}>
                <span className={styles.connectionLabelRow}>
                  <span className={styles.connectionLabel}>{source.label}</span>
                  {comingSoon && (
                    <span className={styles.comingSoonBadge}>Coming soon</span>
                  )}
                </span>
                <span className={styles.connectionDesc}>
                  {source.description}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.uploadSection}>
        <div
          className={mergeClasses(
            styles.contextDropzone,
            isDragOver && styles.contextDropzoneDragOver,
            isSaving && styles.contextDropzoneDisabled,
          )}
          role="button"
          tabIndex={isSaving ? -1 : 0}
          aria-label="Upload context files — accepted: PDF, DOCX, PPTX, XLSX"
          aria-disabled={isSaving}
          onClick={openFilePicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onKeyDown={(e) => {
            if (!isSaving && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              openFilePicker();
            }
          }}
        >
          <span className={styles.contextDropzoneIcon}>
            <ArrowUploadRegular />
          </span>
          <span className={styles.contextDropzonePrimary}>
            Drag and drop or click to upload
          </span>
          <span className={styles.contextDropzoneSub}>
            SOW, proposals, prior deliverables — PDF, DOCX, PPTX, XLSX
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptAttr}
          multiple
          onChange={(e) => {
            if (e.target.files) {
              processFiles(Array.from(e.target.files));
              e.target.value = "";
            }
          }}
          className={styles.contextFileInput}
          disabled={isSaving}
        />

        {fileError && (
          <span className={styles.contextFileError} role="alert">
            {fileError}
          </span>
        )}

        {existingDocuments.length > 0 && (
          <div className={styles.stagedFileList}>
            {existingDocuments.map((doc) => (
              <div key={doc.filename} className={styles.stagedFileRow}>
                <span className={styles.stagedFileName}>
                  {doc.originalFilename}
                </span>
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  className={styles.stagedFileRemove}
                  aria-label={`Remove ${doc.originalFilename}`}
                  onClick={() => onRemoveExistingDocument(doc.filename)}
                  disabled={isSaving}
                />
              </div>
            ))}
          </div>
        )}

        {stagedFiles.length > 0 && (
          <div className={styles.stagedFileList}>
            {stagedFiles.map((file) => (
              <div key={file.name} className={styles.stagedFileRow}>
                <span className={styles.stagedFileName}>{file.name}</span>
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  className={styles.stagedFileRemove}
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemoveFile(file.name)}
                  disabled={isSaving}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
